// pages/karigars/ReturnWork.jsx
// Take finished work back from a karigar. Karigar first: nothing loads until one is
// picked, because the endpoint is scoped to a single OSD_K_LM_ID.
//
// Marking items received is what unlocks them for the customer-delivery step in
// OrderDetail, which gates on OSD_K_IsReceived.
import { useEffect, useMemo, useState } from "react";
import { karigarWorkAPI, karigarsAPI } from "../../services/api";
import { useToast } from "../../contexts/ToastContext";
import { usePagination } from "../../hooks/usePagination";
import { Loading, Pagination, EmptyState, Spinner } from "../../components/ui";
import { fmt, todayISO } from "../../utils/helpers";

const PER_PAGE = 8;

// How long an item has been out with the karigar, in whole days.
function daysOut(iso) {
  if (!iso) return null;
  const issued = new Date(iso + "T00:00:00");
  if (isNaN(issued)) return null;
  return Math.max(0, Math.round((new Date(todayISO() + "T00:00:00") - issued) / 86400000));
}

export default function ReturnWork() {
  const toast = useToast();

  const [karigars, setKarigars] = useState([]);
  const [work, setWork]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState("");

  const [search, setSearch]           = useState("");
  const [karigarId, setKarigarId]     = useState("");
  const [receiveDate, setReceiveDate] = useState(todayISO());
  const [selected, setSelected]       = useState([]);

  useEffect(() => {
    karigarsAPI.list()
      .then(setKarigars)
      .catch((e) => { console.error("[ReturnWork] karigar list error:", e); setKarigars([]); });
  }, []);

  const load = (id) => {
    if (!id) { setWork([]); return; }
    setLoading(true);
    setError("");
    karigarWorkAPI.pendingToReturn(id)
      .then((d) => {
        setWork(d);
        setSelected((prev) => prev.filter((osdId) => d.some((w) => w.osdId === osdId)));
      })
      .catch((e) => {
        console.error("[ReturnWork] load error:", e);
        setWork([]);
        setError(e?.message || "Couldn't load this karigar's pending work");
      })
      .finally(() => setLoading(false));
  };

  // Switching karigar discards the previous selection — those ids belong to someone else
  // and the endpoint validates OSD_ID against OSD_K_LM_ID, so sending them would fail.
  const handleKarigarChange = (id) => {
    setKarigarId(id);
    setSelected([]);
    setSearch("");
    load(id);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return work;
    return work.filter((w) =>
      String(w.orderNo ?? "").toLowerCase().includes(q) ||
      w.customerName.toLowerCase().includes(q) ||
      w.product.toLowerCase().includes(q)
    );
  }, [work, search]);

  const { paged, page, setPage, total, count } = usePagination(filtered, PER_PAGE);

  const filteredIds = filtered.map((w) => w.osdId);
  const allSelected = filteredIds.length > 0 && filteredIds.every((id) => selected.includes(id));
  const toggleAll   = () =>
    setSelected(allSelected ? selected.filter((id) => !filteredIds.includes(id))
                            : [...new Set([...selected, ...filteredIds])]);

  const toggleOne = (id) =>
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const karigar = karigars.find((k) => String(k.id) === String(karigarId));
  const canSave = !!karigarId && !!receiveDate && selected.length > 0 && !saving;

  const handleReceive = async () => {
    setSaving(true);
    setError("");
    try {
      await karigarWorkAPI.receive({ karigarId, receiveDate, osdIds: selected });
      const n = selected.length;
      toast(`${n} item${n > 1 ? "s" : ""} received from ${karigar?.name ?? "karigar"} 📥`);
      setSelected([]);
      load(karigarId);
    } catch (e) {
      console.error("[ReturnWork] receive error:", e);
      setError(e?.message || "Failed to receive work");
      toast("Nothing was received — please try again", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* Who + when */}
      <div className="card" style={{ marginBottom: 18 }}>
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Receive from Karigar <span className="form-required">*</span></label>
            <select
              className="form-input"
              value={karigarId}
              onChange={(e) => handleKarigarChange(e.target.value)}
              disabled={saving}
            >
              <option value="">Select a karigar…</option>
              {karigars.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.name}{k.workType ? ` · ${k.workType}` : ""} — ₹{(k.balance || 0).toLocaleString()} due
                </option>
              ))}
            </select>
            {karigar && (
              <span className="td-muted" style={{ fontSize: 12, marginTop: 4, display: "block" }}>
                {karigar.phone || "No phone"}{karigar.speciality ? ` · ${karigar.speciality}` : ""}
              </span>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Receive Date <span className="form-required">*</span></label>
            <input
              type="date"
              className="form-input"
              value={receiveDate}
              onChange={(e) => setReceiveDate(e.target.value)}
              disabled={saving || !karigarId}
            />
          </div>
        </div>

        {error && (
          <div
            style={{
              background: "var(--red-dim)", border: "1px solid rgba(224,85,85,0.3)",
              borderRadius: "var(--radius-sm)", padding: "12px 14px", marginTop: 4,
              fontSize: 13, color: "var(--red)", lineHeight: 1.6,
            }}
          >
            ❌ {error}
          </div>
        )}
      </div>

      {/* Their pending work */}
      {!karigarId ? (
        <div className="card">
          <EmptyState
            icon="🧵"
            title="Select a karigar"
            subtitle="Pick a karigar above to see the work they still have with them."
          />
        </div>
      ) : (
        <div className="card">
          <div className="card-header">
            <div className="filter-bar" style={{ width: "100%" }}>
              <div className="search-bar" style={{ flex: 1, maxWidth: 320 }}>
                <span className="search-icon">🔍</span>
                <input
                  className="form-input"
                  placeholder="Search order no, customer or item…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <span className="td-muted" style={{ fontSize: 12.5 }}>
                {count} item{count !== 1 ? "s" : ""} out
                {selected.length > 0 ? ` · ${selected.length} selected` : ""}
              </span>
            </div>
          </div>

          {loading ? (
            <Loading />
          ) : (
            <>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}>
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={toggleAll}
                          disabled={saving || filtered.length === 0}
                          title="Select all matching items"
                        />
                      </th>
                      <th>Order No</th>
                      <th>Order Date</th>
                      <th>Customer</th>
                      <th>Item</th>
                      <th>Issued On</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paged.length === 0 ? (
                      <tr>
                        <td colSpan={6}>
                          <EmptyState
                            icon="📥"
                            title={search ? "Nothing matches your search" : "Nothing outstanding"}
                            subtitle={!search ? `${karigar?.name ?? "This karigar"} has returned everything.` : undefined}
                          />
                        </td>
                      </tr>
                    ) : (
                      paged.map((w) => {
                        const out = daysOut(w.issueDate);
                        return (
                          <tr key={w.osdId}>
                            <td>
                              <input
                                type="checkbox"
                                checked={selected.includes(w.osdId)}
                                onChange={() => toggleOne(w.osdId)}
                                disabled={saving}
                              />
                            </td>
                            <td style={{ color: "var(--gold)" }}><strong>#{w.orderNo}</strong></td>
                            <td className="td-muted">{fmt(w.orderDate)}</td>
                            <td>{w.customerName || "—"}</td>
                            <td>{w.product || "—"}</td>
                            <td>
                              {fmt(w.issueDate)}
                              {out !== null && (
                                <div
                                  className="td-muted"
                                  style={{ fontSize: 12, color: out > 7 ? "var(--red)" : undefined }}
                                >
                                  {out === 0 ? "today" : `${out} day${out > 1 ? "s" : ""} ago`}
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              <Pagination page={page} total={total} setPage={setPage} count={count} perPage={PER_PAGE} />
            </>
          )}

          {work.length > 0 && (
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
              <button className="btn btn-primary btn-lg" onClick={handleReceive} disabled={!canSave}>
                {saving ? <><Spinner /> Receiving…</> : `📥 Receive Selected (${selected.length})`}
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
